import React, { useEffect, useRef, useMemo, memo, useCallback, useState } from 'react';
import {
  Typography,
  TextField,
  Box,
  Card,
  CardContent,
  Button,
  Switch,
  FormControlLabel,
  Tabs,
  Tab,
  Chip,
  IconButton,
  Grid,
  Divider,
  Alert,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  ToggleButtonGroup,
  ToggleButton,
  Autocomplete,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  MenuItem,
  Snackbar
} from '@mui/material';
import {
  Save as SaveIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  LocationOn as LocationIcon,
  PhotoCamera as PhotoIcon,
  ExpandMore as ExpandMoreIcon,
  DragIndicator as DragIndicatorIcon,
  Publish as PublishIcon,
  ContentCopy as ContentCopyIcon,
  OpenInNew as OpenInNewIcon
} from '@mui/icons-material';
import { useParams } from 'react-router';
import { useTranslate } from '@refinedev/core';
import { QuillEditor } from '../../components/QuillEditor';
import {
  PropertyDetailsType,
  PropertyDetailsSize,
  PropertyFAQ,
  PropertyPhoto,
  type PropertyDetails as PropertyDetail
} from '../../interfaces/property';
import { usePropertyDetailsViewModel } from './PropertyDetailsViewModel';
import { SortableDetail } from './SortableDetail';
import { apiJson, apiVersion, getCookie } from '../../utils/api';
import { getPropertyShareUrl } from '../../utils/shareUrl';
import analyticsManager from '../../utils/analytics';
import * as Sentry from '@sentry/react';
import { normalizePhotosWithMain } from '../../utils/photos';
import { PropertyStatusChip } from '../../components/property/status';
import { SubscriptionCard } from '../../components/property/subscription-card';

// Mapbox imports
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// dnd-kit for drag and drop
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragEndEvent } from '@dnd-kit/core';
import { SortableContext, rectSortingStrategy, useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

// Set Mapbox access token
mapboxgl.accessToken = import.meta.env.VITE_APP_MAP_ID;

const AVAILABLE_LANGUAGES = [
  { code: 'en' },
  { code: 'es' },
  { code: 'de' },
  { code: 'fr' },
  { code: 'it' },
  { code: 'pt' },
  { code: 'nl' },
  { code: 'hu' },
  { code: 'ru' }
];

const quillModules = {
  toolbar: [
    [{ header: [1, 2, 3, false] }],
    ['bold', 'italic', 'underline', 'strike'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    [{ color: [] }, { background: [] }],
    ['link', 'image'],
    ['clean']
  ]
};

const quillFormats = [
  'header',
  'bold',
  'italic',
  'underline',
  'strike',
  'list',
  'color',
  'background',
  'link',
  'image'
];

// Sortable FAQ item - moved outside component for performance
interface SortableFAQProps {
  id: string;
  index: number;
  faq: PropertyFAQ;
  selectedLanguage: string;
  isAnyDragging: boolean;
  onRemove: (index: number) => void;
  onUpdateQuestion: (index: number, value: string) => void;
  onUpdateAnswer: (index: number, html: string) => void;
  markdownToHtml: (markdown: string) => string;
}

const SortableFAQComponent: React.FC<SortableFAQProps> = ({
  id,
  index,
  faq,
  selectedLanguage,
  isAnyDragging,
  onRemove,
  onUpdateQuestion,
  onUpdateAnswer,
  markdownToHtml
}) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: 'grab'
  } as React.CSSProperties;

  const handleQuestionChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      onUpdateQuestion(index, e.target.value);
    },
    [index, onUpdateQuestion]
  );

  const handleRemove = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      onRemove(index);
    },
    [index, onRemove]
  );

  const faqHtml = useMemo(() => {
    if (!faq.answer || !faq.answer.trim()) return '';
    const isHtml = faq.answer.includes('<') && faq.answer.includes('>');
    return isHtml ? faq.answer : markdownToHtml(faq.answer);
  }, [faq.answer, markdownToHtml]);

  const handleFaqAnswerChange = useCallback(
    (html: string) => {
      onUpdateAnswer(index, html || '');
    },
    [index, onUpdateAnswer]
  );

  return (
    <Accordion ref={setNodeRef} style={style} sx={{ mb: 1 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', color: 'text.secondary', cursor: 'grab' }}
            {...attributes}
            {...listeners}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
          >
            <DragIndicatorIcon fontSize="small" />
          </Box>
          <TextField
            size="small"
            value={faq.question}
            onChange={handleQuestionChange}
            onClick={e => e.stopPropagation()}
            onMouseDown={e => e.stopPropagation()}
            sx={{ flex: 1 }}
          />
          <IconButton size="small" onClick={handleRemove} color="error">
            <DeleteIcon />
          </IconButton>
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ display: isAnyDragging ? 'none' : 'block' }}>
        {!isAnyDragging && (
          <QuillEditor
            key={`${selectedLanguage}-faq-${index}`}
            value={faqHtml}
            onChange={handleFaqAnswerChange}
            modules={quillModules}
            formats={quillFormats}
            style={{ minHeight: 200 }}
          />
        )}
      </AccordionDetails>
    </Accordion>
  );
};

const SortableFAQ = memo(SortableFAQComponent);

// Sortable Photo - moved outside component for performance
interface SortablePhotoProps {
  photo: PropertyPhoto;
  isMain: boolean;
  onRemove: () => void;
  onSetMain: () => void;
  t: (key: string) => string;
}

const SortablePhotoComponent: React.FC<SortablePhotoProps> = ({ photo, isMain, onRemove, onSetMain, t }) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: photo.url });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.6 : 1,
    cursor: 'grab'
  } as React.CSSProperties;

  return (
    <Grid item xs={6} sm={4} md={3} lg={2} ref={setNodeRef} style={style} {...attributes} {...listeners}>
      <Box sx={{ position: 'relative' }}>
        <img
          src={photo.url}
          alt={'Photo'}
          style={{
            width: '100%',
            height: 120,
            objectFit: 'cover',
            borderRadius: 8,
            outline: isMain ? '2px solid #00A39B' : 'none'
          }}
        />
        <IconButton
          size="small"
          sx={{
            position: 'absolute',
            top: 4,
            right: 4,
            backgroundColor: 'rgba(0,0,0,0.5)',
            color: 'white',
            '&:hover': { backgroundColor: 'rgba(0,0,0,0.7)' }
          }}
          onClick={onRemove}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
        <Box sx={{ position: 'absolute', bottom: 4, left: 4 }}>
          <Chip
            size="small"
            label={isMain ? t('properties.photos.main') : t('properties.actions.setAsMain')}
            color={isMain ? 'primary' : 'default'}
            onClick={onSetMain}
            sx={{ backgroundColor: isMain ? 'primary.main' : 'rgba(0,0,0,0.6)', color: 'white' }}
          />
        </Box>
      </Box>
    </Grid>
  );
};

const SortablePhoto = memo(SortablePhotoComponent);

export const PropertyDetails: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const mapContainer = useRef<HTMLDivElement>(null);
  const t = useTranslate();

  // WP-1: Share-link copy handler (details page) — closes over the
  // property uid only, so it can be defined before the VM destructure.
  const [shareLinkSnackbarOpen, setShareLinkSnackbarOpen] = useState(false);

  const handleCopyShareLinkDetails = useCallback(async (target: { uid: string; nano_id?: string }) => {
    const url = getPropertyShareUrl(target.nano_id);
    if (!url) return;
    try {
      await navigator.clipboard.writeText(url);
    } catch (err) {
      Sentry.captureException(err, {
        tags: { module: 'share_link_copy_details', property_uid: target.uid }
      });
      return;
    }
    setShareLinkSnackbarOpen(true);
    analyticsManager.trackCustomEvent('share_link_copied', {
      property_uid: target.uid,
      source: 'details'
    });
  }, []);

  // Profile picker state for admin creating on behalf of another user
  // (hasFullAccess === true). Empty string means "no profile picked" and
  // translates to "no profile_id in the POST body" inside handleSave.
  const [profileId, setProfileId] = useState<string>('');
  const [profileOptions, setProfileOptions] = useState<Array<{ id: string; name: string; photo?: { url?: string } }>>(
    []
  );
  const [profileSearchText, setProfileSearchText] = useState('');
  const [profileLoading, setProfileLoading] = useState(false);

  // Profile search handler. Mirrors the pattern used by PropertiesList.
  const handleProfileSearch = async (searchValue: string) => {
    const trimmedValue = typeof searchValue === 'string' ? searchValue.trim() : '';
    if (!trimmedValue || trimmedValue.length < 3) {
      setProfileOptions([]);
      return;
    }
    setProfileLoading(true);
    try {
      const params = new URLSearchParams({
        search: trimmedValue,
        search_field: 'name',
        page: '1',
        limit: '10'
      });
      const response = await apiJson<{
        data: Array<{ id: string; name: string; photo?: { url?: string } }>;
      }>(`admin/${apiVersion}/profiles?${params.toString()}`);
      setProfileOptions(response.data || []);
    } catch (error) {
      console.error('Failed to search profiles:', error);
      setProfileOptions([]);
    } finally {
      setProfileLoading(false);
    }
  };

  // Use view model for all business logic
  const viewModel = usePropertyDetailsViewModel(id);
  const {
    property,
    hasFullAccess,
    selectedLanguage,
    searchQuery,
    searchResults,
    isLoading,
    saveStatus,
    publishStatus,
    publishUrl,
    publishInvoicePdf,
    publishUrlModalOpen,
    publishConfirmModalOpen,
    publishIsFree,
    uploadDialogOpen,
    newLanguageDialogOpen,
    newLanguageCode,
    isAnyDragging,
    isDirty,
    applyDiscount,
    discountPeriod,
    map,
    marker,
    currentUser,
    setSearchQuery,
    setUploadDialogOpen,
    setPublishUrlModalOpen,
    setPublishConfirmModalOpen,
    setNewLanguageDialogOpen,
    setNewLanguageCode,
    setIsAnyDragging,
    setApplyDiscount,
    setDiscountPeriod,
    loadProperty,
    handleSave,
    handleSaveAndPreview,
    handlePreviewOnly,
    previewStatus,
    previewError,
    setPreviewError,
    handlePublish,
    handlePublishConfirm,
    handleLanguageChange,
    addLanguage,
    removeLanguage,
    searchLocation,
    selectLocation,
    handlePhotosDragEnd,
    setMainPhoto,
    removePhoto,
    updateContent,
    addDetail,
    updateDetail,
    removeDetail,
    handleDetailsDragEnd,
    addFAQ,
    updateFAQ,
    removeFAQ,
    handleFaqsDragEnd,
    updatePropertyField,
    updateCoordinates,
    markdownToHtml,
    htmlToMarkdown
  } = viewModel;

  // WP-2: Save & Preview wrapper that opens the snackbar with the
  // 'previewOpened' / 'previewError' message and emits analytics.
  const [previewSnackbarOpen, setPreviewSnackbarOpen] = useState(false);
  const handleSaveAndPreviewClicked = useCallback(
    async (profileIdForSave?: string) => {
      const outcome = await handleSaveAndPreview(profileIdForSave);
      if (outcome === 'opened') {
        analyticsManager.trackCustomEvent('preview_opened', {
          property_uid: property.uid
        });
      } else {
        analyticsManager.trackCustomEvent('preview_failed', {
          property_uid: property.uid,
          reason: previewError ?? 'unknown'
        });
      }
      setPreviewSnackbarOpen(true);
    },
    [handleSaveAndPreview, previewError, property.uid]
  );

  // Preview-only wrapper: re-uses the same analytics + snackbar plumbing so
  // both flows report preview_opened / preview_failed consistently.
  const handlePreviewOnlyClicked = useCallback(async () => {
    const outcome = await handlePreviewOnly();
    if (outcome === 'opened') {
      analyticsManager.trackCustomEvent('preview_opened', {
        property_uid: property.uid
      });
    } else {
      analyticsManager.trackCustomEvent('preview_failed', {
        property_uid: property.uid,
        reason: previewError ?? 'unknown'
      });
    }
    setPreviewSnackbarOpen(true);
  }, [handlePreviewOnly, previewError, property.uid]);

  const toEditorHtml = useCallback(
    (value: string) => {
      if (!value || !value.trim()) return '';
      const isHtml = value.includes('<') && value.includes('>');
      return isHtml ? value : markdownToHtml(value);
    },
    [markdownToHtml]
  );

  const isFounderOrAdmin = currentUser?.type === 'FOUNDER' || currentUser?.is_admin;

  // dnd sensors
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  // Memoize currentContent to avoid recalculating on every render
  const currentContent = useMemo(() => {
    return property.content[selectedLanguage];
  }, [property.content, selectedLanguage]);

  // See QuillEditor for the rationale; the description + directions editors
  // need the same lastHtmlRef guard the FAQ editor had at the react-quill level.
  const lastDescriptionHtmlRef = useRef<string>('');
  useEffect(() => {
    lastDescriptionHtmlRef.current = toEditorHtml(currentContent?.description ?? '');
  }, [currentContent?.description, toEditorHtml]);
  const handleDescriptionChange = useCallback(
    (html: string) => {
      const normalizedNew = html || '';
      const normalizedCurrent = lastDescriptionHtmlRef.current || '';
      if (normalizedNew !== normalizedCurrent) {
        lastDescriptionHtmlRef.current = normalizedNew;
        // Storage is markdown; convert before reaching state.
        updateContent('description', htmlToMarkdown(normalizedNew));
      }
    },
    [updateContent, htmlToMarkdown]
  );

  const lastDetailContentsRef = useRef<Record<number, string>>({});
  const handleDetailContentsChange = useCallback(
    (index: number, html: string) => {
      const normalizedNew = html || '';
      const normalizedCurrent = lastDetailContentsRef.current[index] ?? '';
      if (normalizedNew !== normalizedCurrent) {
        lastDetailContentsRef.current = {
          ...lastDetailContentsRef.current,
          [index]: normalizedNew
        };
        updateDetail(index, 'contents', htmlToMarkdown(normalizedNew));
      }
    },
    [updateDetail, htmlToMarkdown]
  );
  useEffect(() => {
    lastDetailContentsRef.current = {};
  }, [selectedLanguage]);

  // Add Mapbox CSS styles
  useEffect(() => {
    const mapboxStyles = `
      .mapboxgl-map {
        border-radius: 8px;
      }
      .mapboxgl-ctrl-top-right {
        top: 10px;
        right: 10px;
      }
      .mapboxgl-ctrl-group {
        border-radius: 4px;
      }
    `;

    const style = document.createElement('style');
    style.textContent = mapboxStyles;
    document.head.appendChild(style);

    return () => {
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  // Initialize map
  useEffect(() => {
    if (mapContainer.current && !map.current) {
      // Add a small delay to ensure DOM is fully ready
      const timeoutId = setTimeout(() => {
        if (!mapContainer.current) return; // Double-check container still exists

        try {
          map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: 'mapbox://styles/getlostworld/cm8t7p3ci00gq01sbdifob9vb',
            center: [property.coordinates.lon, property.coordinates.lat],
            zoom: 12
          });

          // Add navigation controls
          map.current.addControl(new mapboxgl.NavigationControl());

          // Add marker
          marker.current = new mapboxgl.Marker({ draggable: true })
            .setLngLat([property.coordinates.lon, property.coordinates.lat])
            .addTo(map.current);

          // Handle marker drag
          marker.current.on('dragend', () => {
            const lngLat = marker.current?.getLngLat();
            if (lngLat) {
              updateCoordinates({ lon: lngLat.lng, lat: lngLat.lat });
            }
          });

          // Handle map click
          map.current.on('click', (e: mapboxgl.MapMouseEvent) => {
            const { lng, lat } = e.lngLat;
            if (marker.current) {
              marker.current.setLngLat([lng, lat]);
              updateCoordinates({ lon: lng, lat: lat });
            }
          });
        } catch (error) {
          console.error('Failed to initialize Mapbox:', error);
        }
      }, 100);

      return () => clearTimeout(timeoutId);
    }

    return () => {
      if (map.current) {
        map.current.remove();
        map.current = null;
      }
    };
  }, [updateCoordinates, property.coordinates]); // Add property.coordinates as dependency

  useEffect(() => {
    if (map.current && marker.current && property.coordinates.lon !== 0 && property.coordinates.lat !== 0) {
      const { lon, lat } = property.coordinates;
      map.current.flyTo({ center: [lon, lat], zoom: 15 });
      marker.current.setLngLat([lon, lat]);
    }
  }, [property.coordinates.lon, property.coordinates.lat]);

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    const remainingSlots = Math.max(0, 20 - property.photos.length);
    const filesToUpload = Array.from(files).slice(0, remainingSlots);
    if (filesToUpload.length === 0) {
      setUploadDialogOpen(false);
      return;
    }

    const formData = new FormData();
    filesToUpload.forEach(file => {
      formData.append(file.name, file, file.name);
    });

    try {
      const apiBase = String((import.meta as any).env?.VITE_API ?? '');
      const base = apiBase.endsWith('/') ? apiBase : `${apiBase}/`;
      const url = `${base}admin/${apiVersion}/image/property`;
      const token = getCookie('accessToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : undefined;

      const response = await fetch(url, {
        method: 'POST',
        body: formData,
        headers,
        credentials: 'include'
      });

      if (!response.ok) {
        const text = await response.text().catch(() => '');
        throw new Error(`Upload failed: ${response.status} ${response.statusText}${text ? ` - ${text}` : ''}`);
      }

      const data = (await response.json()) as Record<string, any>;
      const orderedUploads = filesToUpload.map(file => data[file.name]).filter(Boolean);
      const uploads = orderedUploads.length ? orderedUploads : Object.values(data);
      const baseOrder = property.photos.length;
      const normalizedUploads = uploads.map((photo: any, idx: number) => ({
        ...photo,
        aspect_ratio:
          typeof photo.aspect_ratio === 'number' && !Number.isNaN(photo.aspect_ratio)
            ? photo.aspect_ratio
            : photo.width && photo.height
            ? photo.width / photo.height
            : 1,
        order: baseOrder + idx
      }));

      const nextPhotos = [...property.photos, ...normalizedUploads];
      const { photos: orderedPhotos, main } = normalizePhotosWithMain(
        nextPhotos,
        property.photo ?? normalizedUploads[0]
      );
      updatePropertyField('photos', orderedPhotos);
      if (main) {
        updatePropertyField('photo', main);
      }

      setUploadDialogOpen(false);
    } catch (error) {
      console.error('Photo upload failed:', error);
    } finally {
      event.target.value = '';
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 1200, margin: '0 auto' }}>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mb: 3 }}>
        <Typography variant="h4" sx={{ mb: 1 }}>
          {property.uid ? t('properties.titles.editProperty') : t('properties.titles.createNew')}
        </Typography>
      </Box>

      {saveStatus === 'success' && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {t('properties.notifications.saveSuccess')}
        </Alert>
      )}

      {saveStatus === 'error' && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {t('properties.notifications.saveError')}
        </Alert>
      )}

      <Grid container spacing={3}>
        {/* Subscription Information - Only visible for FOUNDER or admin */}
        {(currentUser?.type === 'FOUNDER' || property.is_admin) && property.subscription && (
          <Grid item xs={12}>
            <SubscriptionCard
              subscription={property.subscription}
              propertyUid={property.uid}
              canManageSubscription={currentUser?.type === 'FOUNDER' || property.is_admin === true}
              onActionComplete={() => id && loadProperty(id)}
            />
          </Grid>
        )}

        {/* Profile picker — only meaningful at creation time. Once a
            property exists, its owner profile is fixed (changing it
            post-hoc would silently move the property between profiles).
            Admins / founders see the picker only on /properties/new
            (i.e. when there is no :id in the URL). */}
        {hasFullAccess && !id && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('properties.sections.profileOwner')}
                </Typography>
                <Autocomplete
                  options={profileOptions}
                  getOptionLabel={option => `${option.name} (${option.id})`}
                  onChange={(e, newValue) => setProfileId(newValue?.id || '')}
                  inputValue={profileSearchText}
                  onInputChange={(e, newInputValue) => {
                    setProfileSearchText(newInputValue);
                    handleProfileSearch(newInputValue);
                  }}
                  loading={profileLoading}
                  sx={{ width: { xs: '100%', sm: 400 } }}
                  renderInput={params => (
                    <TextField
                      {...params}
                      label={t('properties.filters.profile')}
                      placeholder="Search profile..."
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: (
                          <>
                            {profileLoading ? <CircularProgress color="inherit" size={20} /> : null}
                            {params.InputProps.endAdornment}
                          </>
                        )
                      }}
                    />
                  )}
                  renderOption={(props, option) => (
                    <li {...props} style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <Box>
                        <Typography variant="body2">{option.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {option.id}
                        </Typography>
                      </Box>
                    </li>
                  )}
                />
              </CardContent>
            </Card>
          </Grid>
        )}

        {/* Basic Information */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('properties.sections.basicInformation')}</Typography>
                {property.status && <PropertyStatusChip value={property.status} size="medium" />}
              </Box>

              <TextField
                fullWidth
                label={t('properties.fields.name.label')}
                value={property.name}
                onChange={e => updatePropertyField('name', e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label={t('properties.fields.contactPhone.label')}
                value={property.contact}
                onChange={e => updatePropertyField('contact', e.target.value)}
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label={t('properties.fields.website.label')}
                value={property.website}
                onChange={e => updatePropertyField('website', e.target.value)}
                sx={{ mb: 2 }}
              />

              {/* <FormControlLabel
                control={
                  <Switch
                    checked={property.active}
                    onChange={(e) => updatePropertyField("active", e.target.checked)}
                  />
                }
                label="Active"
              /> */}
            </CardContent>
          </Card>
        </Grid>

        {/* Location */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('properties.sections.location')}
              </Typography>

              <Autocomplete
                freeSolo
                options={searchResults}
                getOptionLabel={(option: string | { place_name: string; center: [number, number] }) =>
                  typeof option === 'string' ? option : option.place_name || ''
                }
                inputValue={searchQuery}
                onInputChange={(event, newInputValue) => {
                  setSearchQuery(newInputValue);
                  searchLocation(newInputValue);
                }}
                onChange={(event, newValue) => {
                  if (newValue && typeof newValue !== 'string') {
                    selectLocation(newValue);
                  }
                }}
                renderInput={params => (
                  <TextField
                    {...params}
                    label={t('properties.fields.location.searchLocation')}
                    fullWidth
                    sx={{ mb: 2 }}
                  />
                )}
                renderOption={(props: any, option: { place_name: string; center: [number, number] }) => (
                  <Box component="li" {...props}>
                    <LocationIcon sx={{ mr: 1 }} />
                    {option.place_name}
                  </Box>
                )}
              />

              <TextField
                fullWidth
                label={t('properties.fields.location.address')}
                value={property.address}
                onChange={e => updatePropertyField('address', e.target.value)}
                sx={{ mb: 2 }}
              />

              <Box sx={{ height: 300, mb: 2, border: '1px solid #ccc', borderRadius: '8px' }}>
                <div
                  ref={mapContainer}
                  style={{
                    height: '100%',
                    width: '100%',
                    borderRadius: 8,
                    // Use transparent to avoid white flash while map initializes
                    backgroundColor: 'transparent'
                  }}
                />
              </Box>

              <Typography variant="body2" color="text.secondary">
                {t('properties.fields.coordinates.label')}: {property.coordinates.lat.toFixed(6)},{' '}
                {property.coordinates.lon.toFixed(6)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Photos */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">
                  {t('properties.labels.photosCount', { count: property.photos.length })}
                </Typography>
                <Button
                  variant="outlined"
                  startIcon={<PhotoIcon />}
                  onClick={() => setUploadDialogOpen(true)}
                  disabled={property.photos.length >= 20}
                >
                  {t('properties.actions.addPhotos')}
                </Button>
              </Box>

              <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handlePhotosDragEnd}>
                <SortableContext
                  items={property.photos.map((p: PropertyPhoto) => p.url)}
                  strategy={rectSortingStrategy}
                >
                  <Grid container spacing={2}>
                    {property.photos.map((photo: PropertyPhoto, index: number) => (
                      <SortablePhoto
                        key={photo.url}
                        photo={photo}
                        isMain={property.photo?.url === photo.url || (!property.photo && index === 0)}
                        onRemove={() => removePhoto(index)}
                        onSetMain={() => setMainPhoto(photo)}
                        t={t}
                      />
                    ))}
                  </Grid>
                </SortableContext>
              </DndContext>
            </CardContent>
          </Card>
        </Grid>

        {/* Default Language Selection */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                {t('properties.labels.defaultLanguage')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.keys(property.content).map(langCode => (
                  <Chip
                    key={langCode}
                    label={t(`properties.fields.languages.${langCode}`)}
                    onClick={() => updatePropertyField('default_language', langCode)}
                    color={property.default_language === langCode ? 'primary' : 'default'}
                    variant={property.default_language === langCode ? 'filled' : 'outlined'}
                  />
                ))}
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* Languages */}
        <Grid item xs={12}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Typography variant="h6">{t('properties.labels.contentByLanguage')}</Typography>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={() => setNewLanguageDialogOpen(true)}>
                  {t('properties.actions.addLanguage')}
                </Button>
              </Box>

              <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
                <Tabs value={selectedLanguage} onChange={handleLanguageChange}>
                  {Object.keys(property.content).map(langCode => (
                    <Tab
                      key={langCode}
                      label={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          {t(`properties.fields.languages.${langCode}`)}
                          {Object.keys(property.content).length > 1 && (
                            <IconButton
                              size="small"
                              onClick={e => {
                                e.stopPropagation();
                                removeLanguage(langCode);
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          )}
                        </Box>
                      }
                      value={langCode}
                    />
                  ))}
                </Tabs>
              </Box>

              {selectedLanguage && currentContent && (
                <Box>
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('properties.labels.description')}
                    </Typography>
                    <QuillEditor
                      key={`${selectedLanguage}-description`}
                      value={toEditorHtml(currentContent.description)}
                      onChange={handleDescriptionChange}
                      modules={quillModules}
                      formats={quillFormats}
                      // Use minHeight so the toolbar + content are not clipped
                      style={{ minHeight: 200 }}
                    />
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('properties.labels.details')}
                    </Typography>
                    <Button
                      variant="outlined"
                      startIcon={<AddIcon />}
                      onClick={addDetail}
                      sx={{ mb: 2, display: 'none' }}
                    >
                      {t('properties.actions.addDetail')}
                    </Button>

                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={() => setIsAnyDragging(true)}
                      onDragCancel={() => setIsAnyDragging(false)}
                      onDragEnd={handleDetailsDragEnd}
                    >
                      <SortableContext
                        items={currentContent.details.map((_: PropertyDetail, i: number) => String(i))}
                        strategy={rectSortingStrategy}
                      >
                        {currentContent.details.map((detail: PropertyDetail, index: number) => (
                          <SortableDetail
                            key={`detail-${index}`}
                            id={String(index)}
                            index={index}
                            detail={detail}
                            isAnyDragging={isAnyDragging}
                            onUpdate={updateDetail}
                            onRemove={removeDetail}
                            renderEditor={i => (
                              <QuillEditor
                                key={`${selectedLanguage}-detail-${i}`}
                                value={toEditorHtml(currentContent.details[i].contents)}
                                onChange={html => handleDetailContentsChange(i, html)}
                                modules={quillModules}
                                formats={quillFormats}
                                // Use minHeight so the toolbar + content are not clipped
                                style={{ minHeight: 200 }}
                              />
                            )}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </Box>

                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      {t('properties.labels.faq')}
                    </Typography>
                    <Button variant="outlined" startIcon={<AddIcon />} onClick={addFAQ} sx={{ mb: 2 }}>
                      {t('properties.actions.addFaq')}
                    </Button>
                    <DndContext
                      sensors={sensors}
                      collisionDetection={closestCenter}
                      onDragStart={() => setIsAnyDragging(true)}
                      onDragCancel={() => setIsAnyDragging(false)}
                      onDragEnd={handleFaqsDragEnd}
                    >
                      <SortableContext
                        items={currentContent.faq.map((_: PropertyFAQ, i: number) => String(i))}
                        strategy={rectSortingStrategy}
                      >
                        {currentContent.faq.map((faq: PropertyFAQ, index: number) => (
                          <SortableFAQ
                            key={`faq-${index}`}
                            id={String(index)}
                            index={index}
                            faq={faq}
                            selectedLanguage={selectedLanguage}
                            isAnyDragging={isAnyDragging}
                            onRemove={removeFAQ}
                            onUpdateQuestion={(i, val) => updateFAQ(i, 'question', val)}
                            onUpdateAnswer={(i, html) => updateFAQ(i, 'answer', html)}
                            markdownToHtml={markdownToHtml}
                          />
                        ))}
                      </SortableContext>
                    </DndContext>
                  </Box>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>

        {/* Metadata */}
        {property.uid && (
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('properties.metadata.title')}
                </Typography>
                <Grid container spacing={2}>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {t('properties.metadata.createdDate')}
                    </Typography>
                    <Typography variant="body1">{new Date(property.created_date).toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {t('properties.metadata.updatedDate')}
                    </Typography>
                    <Typography variant="body1">{new Date(property.updated_date).toLocaleString()}</Typography>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <Typography variant="body2" color="text.secondary">
                      {t('properties.metadata.nanoId')}
                    </Typography>
                    <Typography variant="body1" sx={{ fontFamily: 'monospace' }}>
                      {property.nano_id}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} md={6}>
                    <Typography variant="body2" color="text.secondary">
                      {t('properties.metadata.shareUrl')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                      {(() => {
                        const shareUrl = getPropertyShareUrl(property.nano_id);
                        const canCopy = shareUrl !== null && property.status === 'published';
                        return (
                          <>
                            <Typography
                              variant="body1"
                              sx={{
                                fontFamily: 'monospace',
                                flex: 1,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap'
                              }}
                            >
                              {shareUrl ?? '—'}
                            </Typography>
                            {canCopy && (
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={() => handleCopyShareLinkDetails(property)}
                                title={t('buttons.copyShareLink')}
                                aria-label={t('buttons.copyShareLink')}
                              >
                                <ContentCopyIcon fontSize="small" />
                              </IconButton>
                            )}
                          </>
                        );
                      })()}
                    </Box>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      {/* Save and Publish Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 2, mt: 3 }}>
        {property.uid && (
          <Button
            variant="contained"
            startIcon={publishStatus === 'redirecting' ? <CircularProgress size={20} /> : <PublishIcon />}
            onClick={handlePublish}
            disabled={
              publishStatus === 'publishing' ||
              publishStatus === 'redirecting' ||
              saveStatus === 'saving' ||
              property.status === 'published'
            }
            size="large"
            color="success"
          >
            {publishStatus === 'publishing'
              ? t('properties.states.publishing')
              : publishStatus === 'redirecting'
              ? t('properties.states.redirecting')
              : t('properties.states.publishProperty')}
          </Button>
        )}
        <Button
          variant="outlined"
          startIcon={
            previewStatus === 'saving' || previewStatus === 'opening' ? (
              <CircularProgress size={20} />
            ) : (
              <OpenInNewIcon />
            )
          }
          onClick={() => (isDirty ? handleSaveAndPreviewClicked(profileId || undefined) : handlePreviewOnlyClicked())}
          disabled={previewStatus !== 'idle' || !property.uid || !property.profile}
          title={!property.uid || !property.profile ? t('properties.states.previewDisabledTooltip') : undefined}
          size="large"
          color="primary"
        >
          {isDirty ? t('properties.states.saveAndPreview') : t('properties.states.previewOnly')}
        </Button>
        <Button
          variant="outlined"
          startIcon={<SaveIcon />}
          onClick={() => handleSave(profileId || undefined)}
          disabled={saveStatus === 'saving' || publishStatus === 'publishing' || publishStatus === 'redirecting'}
          size="large"
          color="primary"
        >
          {saveStatus === 'saving' ? t('properties.states.saving') : t('properties.states.saveProperty')}
        </Button>
      </Box>
      {previewError && (
        <Box sx={{ mt: 2 }}>
          <Alert severity="error" onClose={() => setPreviewError(null)}>
            {previewError || t('properties.states.previewError')}
          </Alert>
        </Box>
      )}

      {/* Publish Confirmation Modal */}
      <Dialog open={publishConfirmModalOpen} onClose={() => setPublishConfirmModalOpen(false)}>
        <DialogTitle>{t('properties.dialogs.publishConfirm.title')}</DialogTitle>
        <DialogContent>
          <Typography variant="body1" sx={{ mb: 2 }}>
            {t('properties.dialogs.publishConfirm.description')}
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            {t('properties.dialogs.publishConfirm.warning')}
          </Alert>
          {isFounderOrAdmin && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
              <FormControlLabel
                control={<Switch checked={applyDiscount} onChange={e => setApplyDiscount(e.target.checked)} />}
                label={t('properties.dialogs.publishConfirm.applyDiscount')}
              />
              {applyDiscount && (
                <Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {t('properties.dialogs.publishConfirm.discountDuration')}
                  </Typography>
                  <ToggleButtonGroup
                    exclusive
                    value={discountPeriod}
                    onChange={(_, val) => setDiscountPeriod(val)}
                    size="small"
                  >
                    <ToggleButton value={3}>{t('properties.dialogs.publishConfirm.period3')}</ToggleButton>
                    <ToggleButton value={6}>{t('properties.dialogs.publishConfirm.period6')}</ToggleButton>
                    <ToggleButton value={12}>{t('properties.dialogs.publishConfirm.period12')}</ToggleButton>
                  </ToggleButtonGroup>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishConfirmModalOpen(false)}>{t('buttons.cancel')}</Button>
          <Button onClick={handlePublishConfirm} variant="contained" color="success">
            {t('buttons.confirm')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Photo Upload Dialog */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)}>
        <DialogTitle>{t('properties.dialogs.uploadPhotos.title')}</DialogTitle>
        <DialogContent>
          <input type="file" multiple accept="image/*" onChange={handlePhotoUpload} style={{ marginTop: 16 }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadDialogOpen(false)}>{t('buttons.cancel')}</Button>
        </DialogActions>
      </Dialog>

      {/* Add Language Dialog */}
      <Dialog
        open={newLanguageDialogOpen}
        onClose={() => {
          setNewLanguageDialogOpen(false);
          setNewLanguageCode('');
        }}
      >
        <DialogTitle>{t('properties.dialogs.addLanguage.title')}</DialogTitle>
        <DialogContent>
          <Autocomplete
            options={AVAILABLE_LANGUAGES.filter(lang => !Object.keys(property.content).includes(lang.code))}
            getOptionLabel={option =>
              `${t(`properties.fields.languages.${option.code}`)} (${option.code.toUpperCase()})`
            }
            value={AVAILABLE_LANGUAGES.find(lang => lang.code === newLanguageCode.toLowerCase()) || null}
            onChange={(_, newValue) => setNewLanguageCode(newValue?.code || '')}
            renderInput={params => (
              <TextField
                {...params}
                autoFocus
                margin="dense"
                label={t('properties.actions.selectLanguage')}
                fullWidth
                variant="outlined"
                helperText={t('properties.dialogs.addLanguage.helper')}
              />
            )}
            sx={{ mt: 1, minWidth: 300 }}
          />
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setNewLanguageDialogOpen(false);
              setNewLanguageCode('');
            }}
          >
            {t('buttons.cancel')}
          </Button>
          <Button
            onClick={() => addLanguage(newLanguageCode.toLowerCase())}
            disabled={!newLanguageCode || Object.keys(property.content).includes(newLanguageCode.toLowerCase())}
          >
            {t('buttons.add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Publish URL Modal */}
      <Dialog open={publishUrlModalOpen} onClose={() => setPublishUrlModalOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('properties.dialogs.publishUrl.title')}</DialogTitle>
        <DialogContent>
          {publishIsFree && (
            <Alert severity="success" sx={{ mb: 2 }}>
              First-property free — Stripe Checkout will show $0
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('properties.dialogs.publishUrl.description')}
          </Typography>
          <TextField
            fullWidth
            value={publishUrl || ''}
            variant="outlined"
            sx={{ mb: 2 }}
            inputProps={{ readOnly: true }}
            InputProps={{
              endAdornment: (
                <IconButton
                  edge="end"
                  onClick={() => {
                    if (publishUrl) {
                      navigator.clipboard.writeText(publishUrl);
                    }
                  }}
                  title={t('buttons.copyToClipboard')}
                >
                  📋
                </IconButton>
              )
            }}
          />
          {publishInvoicePdf && (
            <Button size="small" variant="outlined" href={publishInvoicePdf} target="_blank" sx={{ mt: 2 }}>
              Download PDF
            </Button>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPublishUrlModalOpen(false)}>{t('buttons.close')}</Button>
        </DialogActions>
      </Dialog>

      {/* WP-1 share-link copy snackbar */}
      <Snackbar
        open={shareLinkSnackbarOpen}
        autoHideDuration={2000}
        onClose={() => setShareLinkSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={t('buttons.shareLinkCopied')}
      />

      {/* WP-2 preview-opened snackbar (success only; errors surface inline) */}
      <Snackbar
        open={previewSnackbarOpen && previewStatus === 'idle' && !previewError}
        autoHideDuration={2000}
        onClose={() => setPreviewSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        message={t('properties.states.previewOpened')}
      />
    </Box>
  );
};
